import type { ElementType } from "react";
import { NavLink, useLocation } from "react-router";
import { cn } from "@/lib/cn";

interface Props {
  to: string;
  icon: ElementType;
  label: string;
  collapsed: boolean;
  /** Optional callback fired when the nav item is clicked (e.g., close mobile sidebar). */
  onClick?: () => void;
}

export function NavItem({ to, icon: Icon, label, collapsed, onClick }: Props) {
  const location = useLocation();

  const handleClick = (event: React.MouseEvent) => {
    // Re-clicking the item for the page we're already on must be a no-op:
    // NavLink otherwise navigates to the bare `to` path and drops the current
    // URL query string (e.g. Orders/Routes date-picker filters stored via
    // useUrlFilters). Prevent the default navigation so existing query params
    // are preserved. Navigating to a DIFFERENT page still works normally.
    if (location.pathname === to) {
      event.preventDefault();
    }
    onClick?.();
  };

  return (
    <NavLink
      to={to}
      title={collapsed ? label : undefined}
      onClick={handleClick}
      className={({ isActive }) =>
        cn(
          "relative flex items-center gap-3 rounded-lg px-3 py-2 text-white/50 transition-all duration-150 hover:bg-white/8 hover:text-white/90",
          isActive && "bg-white/[0.12] text-white",
          collapsed && "justify-center px-2",
        )
      }
    >
      {({ isActive }) => (
        <>
          {/* Blue left accent bar for active item */}
          {isActive && (
            <span className="absolute left-0 top-1/2 h-5 w-0.5 -translate-y-1/2 rounded-r-full bg-brand-primary" />
          )}
          <Icon
            size={18}
            className={cn(
              "shrink-0 transition-colors",
              isActive ? "text-brand-primary" : "text-white/50",
            )}
          />
          {!collapsed && (
            <span
              className={cn(
                "truncate text-sm",
                isActive
                  ? "font-semibold text-white"
                  : "font-medium text-white/70",
              )}
            >
              {label}
            </span>
          )}
        </>
      )}
    </NavLink>
  );
}
